import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo, useState } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { z } from "zod";

import { AuthShell, Field, FormError } from "../components/authForm";
import { errorMessage, useCaptcha } from "../lib/authForm";
import { Captcha } from "../components/Captcha";
import { PasswordStrength } from "../components/PasswordStrength";
import type { Strength } from "../lib/passwordStrength";
import { useAuthStore } from "../stores/authStore";
import { usePageMeta } from "../lib/usePageMeta";

// Схемы повторяют ограничения backend'а: ошибку лучше показать до запроса.
// Тексты приходят из перевода, поэтому схема — функция: при смене языка
// сообщения должны меняться вместе с интерфейсом.
const loginSchema = (t: TFunction) =>
  z.object({
    login: z.string().min(1, t("auth.error.loginRequired")),
    password: z.string().min(1, t("auth.error.passwordRequired")),
  });

const registerSchema = (t: TFunction) =>
  z
    .object({
      username: z
        .string()
        .min(3, t("auth.error.usernameShort"))
        .max(32, t("auth.error.usernameLong"))
        .regex(/^[a-zA-Z0-9_-]+$/, t("auth.error.usernameChars")),
      email: z.string().email(t("auth.error.email")),
      password: z.string().min(8, t("auth.error.passwordShort")),
      passwordConfirm: z.string().min(1, t("auth.error.passwordRepeat")),
      acceptPrivacyPolicy: z.literal(true, {
        errorMap: () => ({ message: t("auth.error.privacy") }),
      }),
    })
    // Опечатку в пароле человек заметит только когда не сможет войти, а
    // восстановление уже требует доступа к почте — дешевле поймать здесь.
    .refine((values) => values.password === values.passwordConfirm, {
      message: t("auth.error.passwordMismatch"),
      path: ["passwordConfirm"],
    });

type LoginValues = z.infer<ReturnType<typeof loginSchema>>;
type RegisterValues = z.infer<ReturnType<typeof registerSchema>>;

export function AuthPage({ mode }: { mode: "login" | "register" }) {
  return mode === "login" ? <LoginForm /> : <RegisterForm />;
}

function LoginForm() {
  const { login, status } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation() as { state?: { from?: string } };
  const [serverError, setServerError] = useState<string | null>(null);
  const captcha = useCaptcha();

  const { t } = useTranslation();
  usePageMeta({ title: t("seo.login") });
  const form = useForm<LoginValues>({
    resolver: zodResolver(useMemo(() => loginSchema(t), [t])),
  });

  if (status === "authenticated") return <Navigate to="/quests" replace />;

  return (
    <AuthShell
      title={t("auth.login.title")}
      subtitle={t("auth.login.subtitle")}
      footer={
        <p className="text-sm text-ink-400">
          {t("auth.login.noAccount")}{" "}
          <Link to="/register" className="text-amber-300 hover:text-amber-200">
            {t("auth.login.signUp")}
          </Link>
        </p>
      }
    >
      <form
        className="space-y-4"
        onSubmit={form.handleSubmit(async (values) => {
          setServerError(null);
          try {
            await login(values.login, values.password, captcha.token ?? undefined);
            navigate(location.state?.from ?? "/quests", { replace: true });
          } catch (error) {
            setServerError(errorMessage(error));
            captcha.reset();
          }
        })}
      >
        <Field label={t("auth.field.login")} error={form.formState.errors.login?.message}>
          <input className="field" autoComplete="username" {...form.register("login")} />
        </Field>

        <Field label={t("auth.field.password")} error={form.formState.errors.password?.message}>
          <input
            type="password"
            className="field"
            autoComplete="current-password"
            {...form.register("password")}
          />
          <div className="mt-1.5 text-right">
            <Link
              to="/forgot-password"
              className="text-xs text-ink-400 hover:text-amber-300"
            >
              {t("auth.login.forgot")}
            </Link>
          </div>
        </Field>

        {captcha.required && (
          <Captcha siteKey={captcha.siteKey} onToken={captcha.setToken} />
        )}

        {serverError && <FormError>{serverError}</FormError>}

        <button
          type="submit"
          className="btn-primary w-full"
          disabled={form.formState.isSubmitting || captcha.blocked}
        >
          {form.formState.isSubmitting ? t("auth.login.submitting") : t("auth.login.submit")}
        </button>

        {captcha.blocked && (
          <p className="text-center text-xs text-ink-500">
            {t("auth.captchaHint")}
          </p>
        )}
      </form>
    </AuthShell>
  );
}

function RegisterForm() {
  const { register: registerUser, status } = useAuthStore();
  const navigate = useNavigate();
  const [serverError, setServerError] = useState<string | null>(null);
  const [strength, setStrength] = useState<Strength | null>(null);
  const captcha = useCaptcha();

  // onTouched, а не проверка по кнопке: кнопка заблокирована до прохождения
  // капчи, и несовпадение паролей иначе всплыло бы только после неё.
  const { t } = useTranslation();
  usePageMeta({ title: t("seo.register") });
  const form = useForm<RegisterValues>({
    resolver: zodResolver(useMemo(() => registerSchema(t), [t])),
    mode: "onTouched",
  });

  // Имя и адрес уходят в оценку пароля: пароль из собственных данных
  // подбирают в первую очередь, и сервер такой не примет.
  const username = form.watch("username") ?? "";
  const email = form.watch("email") ?? "";
  const password = form.watch("password") ?? "";
  const passwordConfirm = form.watch("passwordConfirm") ?? "";

  // Совпадение паролей проверяется мимо схемы: refine у zod навешан на весь
  // объект и пропускается, пока хоть одно поле формы невалидно. Иначе человек
  // увидел бы «пароли не совпадают» только заполнив всё остальное.
  const mismatch =
    form.formState.touchedFields.passwordConfirm &&
    passwordConfirm.length > 0 &&
    passwordConfirm !== password;

  const minScore = captcha.settings.data?.passwordMinScore ?? 2;
  const weakPassword = password.length > 0 && strength !== null && !strength.acceptable;

  if (status === "authenticated") return <Navigate to="/quests" replace />;

  return (
    <AuthShell
      title={t("auth.register.title")}
      subtitle={t("auth.register.subtitle")}
      footer={
        <p className="text-sm text-ink-400">
          {t("auth.register.haveAccount")}{" "}
          <Link to="/login" className="text-amber-300 hover:text-amber-200">
            {t("nav.login")}
          </Link>
        </p>
      }
    >
      <form
        className="space-y-4"
        onSubmit={form.handleSubmit(async (values) => {
          setServerError(null);
          try {
            await registerUser({
              username: values.username,
              email: values.email,
              password: values.password,
              acceptPrivacyPolicy: values.acceptPrivacyPolicy,
              captchaToken: captcha.token ?? undefined,
            });
            navigate("/quests", { replace: true });
          } catch (error) {
            setServerError(errorMessage(error));
            captcha.reset();
          }
        })}
      >
        <Field label={t("auth.field.username")} error={form.formState.errors.username?.message}>
          <input className="field" autoComplete="username" {...form.register("username")} />
        </Field>

        <Field label={t("auth.field.email")} error={form.formState.errors.email?.message}>
          <input type="email" className="field" autoComplete="email" {...form.register("email")} />
        </Field>

        <Field label={t("auth.field.password")} error={form.formState.errors.password?.message}>
          <input
            type="password"
            className="field"
            autoComplete="new-password"
            {...form.register("password")}
          />
          <PasswordStrength
            password={password}
            userInputs={[username, email]}
            minScore={minScore}
            onChange={setStrength}
          />
        </Field>

        <Field
          label={t("auth.field.passwordConfirm")}
          error={
            form.formState.errors.passwordConfirm?.message ??
            (mismatch ? t("auth.error.passwordMismatch") : undefined)
          }
        >
          <input
            type="password"
            className="field"
            autoComplete="new-password"
            {...form.register("passwordConfirm")}
          />
        </Field>

        <div>
          <label className="flex cursor-pointer items-start gap-2.5 text-sm text-ink-300">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 shrink-0 accent-amber-400"
              {...form.register("acceptPrivacyPolicy")}
            />
            <span>
              {t("auth.register.privacyPrefix")}{" "}
              <Link
                to="/privacy"
                target="_blank"
                className="text-amber-300 underline underline-offset-2 hover:text-amber-200"
              >
                {t("auth.register.privacyLink")}
              </Link>
            </span>
          </label>
          {form.formState.errors.acceptPrivacyPolicy && (
            <p className="mt-1 text-xs text-rust">
              {form.formState.errors.acceptPrivacyPolicy.message}
            </p>
          )}
        </div>

        {captcha.required && (
          <Captcha siteKey={captcha.siteKey} onToken={captcha.setToken} />
        )}

        {serverError && <FormError>{serverError}</FormError>}

        <button
          type="submit"
          className="btn-primary w-full"
          disabled={form.formState.isSubmitting || captcha.blocked || weakPassword}
        >
          {form.formState.isSubmitting ? t("auth.register.submitting") : t("auth.register.submit")}
        </button>

        {captcha.blocked && (
          <p className="text-center text-xs text-ink-500">
            {t("auth.captchaHint")}
          </p>
        )}
      </form>
    </AuthShell>
  );
}
